import { useCallback, useEffect, useState } from 'react';
import type { PrayerNote } from '../domain.ts';
import { ApiError, isLive, request } from './client.ts';
import { notes as mockNotes } from '../mock.ts';

/**
 * 기도 노트 데이터 소스.
 *
 * 토큰이 있으면 API, 없으면 목업. 어느 쪽인지 호출부가 알 수 있게 source 를 함께 돌려줍니다.
 */

export type Source = 'api' | 'mock';

/**
 * 서버 표현 → 화면 표현.
 *
 * 서버는 noteId 를, 화면은 id 를 씁니다. 목업 모드에서는 드러나지 않던 차이라
 * 실제 연결을 켠 지금 여기서 한 번에 흡수합니다.
 *
 * progress · history 는 이 필드들이 생기기 전에 만들어진 노트에는 없습니다.
 * 없는 것을 빈 배열로 채워 화면이 undefined 를 만나지 않게 합니다.
 */
interface ServerNote extends Omit<PrayerNote, 'id' | 'author' | 'progress' | 'history'> {
  noteId: string;
  authorId: string;
  progress?: PrayerNote['progress'];
  history?: PrayerNote['history'];
}

function toNote(raw: ServerNote): PrayerNote {
  return {
    ...raw,
    id: raw.noteId,
    author: raw.authorId,
    isPrivate: raw.isPrivate ?? false,
    progress: raw.progress ?? [],
    history: raw.history ?? [],
  };
}

export interface NotesState {
  notes: PrayerNote[];
  source: Source;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

export function useNotes(): NotesState {
  const live = isLive();
  const [notes, setNotes] = useState<PrayerNote[]>(live ? [] : mockNotes);
  const [loading, setLoading] = useState(live);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!live) return;

    let alive = true;
    setLoading(true);
    setError(null);

    request<{ notes: ServerNote[] }>('/notes')
      .then((res) => {
        if (!alive) return;
        setNotes(res.notes.map(toNote));
      })
      .catch((err: unknown) => {
        if (!alive) return;
        // 실패했을 때 목업으로 갈아치우지 않습니다.
        // 빈 화면 + 이유가, 가짜 데이터보다 낫습니다.
        setError(err instanceof ApiError ? err.message : '기도 노트를 불러오지 못했습니다.');
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [live, tick]);

  return {
    notes,
    source: live ? 'api' : 'mock',
    loading,
    error,
    reload: useCallback(() => setTick((t) => t + 1), []),
  };
}

/* ── 변경 ────────────────────────────────────────────────────── */

/**
 * 새 기도 노트.
 *
 * 화면에서 받는 것은 제목 · 내용 · 영역 · PLAN · Private 다섯입니다.
 * 기도 상태(기도중)와 중요도(일반)는 서버가 디폴트로 채웁니다.
 */
export interface NewNote {
  title: string;
  body: string;
  category: string;
  plan: PrayerNote['plan'];
  isPrivate: boolean;
}

export async function createNote(input: NewNote): Promise<PrayerNote> {
  const res = await request<{ note: ServerNote }>('/notes', { method: 'POST', body: input });
  return toNote(res.note);
}

/**
 * 부분 수정. 넘긴 필드만 바뀝니다.
 *
 * 대표 기도제목을 켤 때 이미 3개가 차 있으면 서버가 409 와 함께 사용자에게
 * 보여줄 문장을 돌려줍니다. 화면은 그 문장을 그대로 띄우고 토글을 되돌립니다.
 */
export type NotePatch = Partial<
  Pick<
    PrayerNote,
    | 'title'
    | 'body'
    | 'category'
    | 'status'
    | 'priority'
    | 'plan'
    | 'representative'
    | 'isPrivate'
    | 'progress'
  >
>;

export async function patchNote(id: string, changes: NotePatch): Promise<PrayerNote> {
  const res = await request<{ note: ServerNote }>(`/notes/${id}`, {
    method: 'PATCH',
    body: changes,
  });
  return toNote(res.note);
}

export async function deleteNote(id: string): Promise<void> {
  await request<void>(`/notes/${id}`, { method: 'DELETE' });
}
